import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { MapPin, Clock, Camera, CheckCircle2 } from "lucide-react";

const features = [
  "Two fully equipped studio spaces in the heart of Columbus, GA",
  "Professional cyclorama wall, seamless backdrops, and lighting kits",
  "Beauty station, changing rooms, and client lounge",
  "Hourly rentals with same-day availability",
  "Photographers, videographers, podcasters, and brands welcome",
  "Onsite parking with easy load-in access",
];

const faqEntries = [
  {
    q: "Where is your photo studio located in Columbus, GA?",
    a: "MyStudio is located in Columbus, Georgia. Reach out via the contact page for the exact address — directions are sent with your booking confirmation.",
  },
  {
    q: "How much does it cost to rent a photo studio in Columbus, GA?",
    a: "Hourly rates start at our base studio rate and vary by space and add-ons. See the Rates page for full pricing including beauty station, backdrops, and equipment rentals.",
  },
  {
    q: "Can I book the studio by the hour?",
    a: "Yes. We offer hourly rentals with a one-hour minimum and 30-minute extensions when the schedule allows.",
  },
  {
    q: "Do you provide lighting and backdrops?",
    a: "Yes. Every booking includes access to our lighting kits, seamless paper backdrops, and the cyclorama wall. Specialty backdrops can be added at checkout.",
  },
  {
    q: "Is your Columbus, GA studio good for video shoots and podcasts?",
    a: "Absolutely. The spaces are sound-treated for podcast and interview recording and large enough for video production, music videos, and content shoots.",
  },
];

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "PhotographyBusiness",
  name: "MyStudio — Columbus, GA Photo Studio Rental",
  url: "https://bookmystudiospace.com/columbus-ga-photo-studio-rental",
  image: "https://bookmystudiospace.com/favicon.png",
  email: "info@bookmystudiospace.com",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Columbus",
    addressRegion: "GA",
    addressCountry: "US",
  },
  areaServed: "Columbus, GA",
  priceRange: "$$",
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqEntries.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://bookmystudiospace.com/" },
    { "@type": "ListItem", position: 2, name: "Columbus GA Photo Studio Rental", item: "https://bookmystudiospace.com/columbus-ga-photo-studio-rental" },
  ],
};

const ColumbusGaPhotoStudio = () => (
  <div className="pt-20">
    <Helmet>
      <title>Columbus GA Photo Studio Rental | MyStudio</title>
      <meta
        name="description"
        content="Rent a professional photo studio in Columbus, GA. Hourly rentals, cyclorama wall, lighting, backdrops, and beauty station. Book your shoot at MyStudio today."
      />
      <link rel="canonical" href="https://bookmystudiospace.com/columbus-ga-photo-studio-rental" />
      <meta property="og:title" content="Columbus GA Photo Studio Rental | MyStudio" />
      <meta
        property="og:description"
        content="Professional photo studio rental in Columbus, Georgia. Hourly bookings with lighting, cyc wall, and backdrops included."
      />
      <meta property="og:url" content="https://bookmystudiospace.com/columbus-ga-photo-studio-rental" />
      <meta property="og:type" content="website" />
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </Helmet>

    <section className="py-20 text-center border-b border-border">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="inline-flex items-center gap-2 text-primary font-mono text-xs tracking-widest uppercase mb-4">
          <MapPin className="w-4 h-4" /> Columbus, Georgia
        </div>
        <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-6">
          COLUMBUS GA PHOTO STUDIO RENTAL
        </h1>
        <p className="text-muted-foreground font-body text-lg max-w-2xl mx-auto mb-8">
          A professional photo studio in the heart of Columbus, GA — built for photographers,
          videographers, brands, and creators. Hourly bookings, pro lighting, cyc wall, and beauty
          station all included.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/book">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-8">
              Book the Studio
            </Button>
          </Link>
          <Link to="/rates">
            <Button variant="outline" className="border-border font-body px-8">
              View Rates
            </Button>
          </Link>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          WHY RENT AT MYSTUDIO
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {features.map((f) => (
            <div key={f} className="bg-panel border border-border p-5 flex gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm font-body text-foreground/85">{f}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          BUILT FOR EVERY SHOOT
        </h2>
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-panel border border-border p-6 text-center">
            <Camera className="w-6 h-6 text-primary mx-auto mb-3" />
            <h3 className="font-display tracking-widest text-sm mb-2">PHOTOGRAPHY</h3>
            <p className="text-xs text-muted-foreground font-body">
              Portraits, fashion, product, headshots, lookbooks.
            </p>
          </div>
          <div className="bg-panel border border-border p-6 text-center">
            <Camera className="w-6 h-6 text-primary mx-auto mb-3" />
            <h3 className="font-display tracking-widest text-sm mb-2">VIDEO &amp; CONTENT</h3>
            <p className="text-xs text-muted-foreground font-body">
              Music videos, brand content, social, interviews.
            </p>
          </div>
          <div className="bg-panel border border-border p-6 text-center">
            <Clock className="w-6 h-6 text-primary mx-auto mb-3" />
            <h3 className="font-display tracking-widest text-sm mb-2">HOURLY RENTAL</h3>
            <p className="text-xs text-muted-foreground font-body">
              Book by the hour with same-day availability when open.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 border-t border-border">
      <div className="container mx-auto px-4 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-display tracking-widest text-center mb-10">
          FREQUENTLY ASKED QUESTIONS
        </h2>
        <div className="space-y-4">
          {faqEntries.map((f) => (
            <div key={f.q} className="bg-panel border border-border p-6">
              <h3 className="font-display tracking-widest text-primary text-base mb-2">{f.q}</h3>
              <p className="text-sm font-body text-foreground/80">{f.a}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/book">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-10">
              Book Your Columbus, GA Studio Session
            </Button>
          </Link>
          <p className="text-xs font-mono tracking-widest text-muted-foreground mt-8">
            <Link to="/photo-studio-near-me-columbus-ga" className="text-primary hover:underline">Photo studio near me — Columbus, GA</Link>
            {" · "}
            <Link to="/atlanta-studio-space-rental" className="text-primary hover:underline">Coming from Atlanta?</Link>
          </p>
        </div>
      </div>
    </section>
  </div>
);

export default ColumbusGaPhotoStudio;